import { useCallback, useRef, useState } from "react";

import type { CarrierNoteState, SaveState } from "../types";

const createInitialState = (): CarrierNoteState => ({
  noteContent: "",
  saveState: "saved" as SaveState,
  lastSavedTimestamp: null,
  hasContent: false,
});

interface UseCarrierNotesStateResult {
  getState: (carrierId: string) => CarrierNoteState;
  setContent: (carrierId: string, content: string) => void;
  setSaveState: (carrierId: string, state: SaveState) => void;
  markSaved: (carrierId: string) => void;
  markError: (carrierId: string) => void;
  initializeContent: (carrierId: string, content: string) => void;
  seedHasContent: (carrierIds: string[]) => void;
  hasUnsavedChanges: () => boolean;
  getUnsavedCarrierIds: () => string[];
  /** Forces a re-render — call after batch state mutations. */
  tick: () => void;
}

/**
 * Manages independent note state for each carrier.
 *
 * Uses a ref-backed Map for O(1) carrier lookups and avoids
 * re-renders on every keystroke. Call `tick()` when the UI needs
 * to reflect the latest state (e.g. after a save-state transition).
 */
export const useCarrierNotesState = (): UseCarrierNotesStateResult => {
  const stateMap = useRef(new Map<string, CarrierNoteState>());
  const seededCarrierIds = useRef(new Set<string>());
  const [, setVersion] = useState(0);

  const tick = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  const getState = useCallback((carrierId: string): CarrierNoteState => {
    const existing = stateMap.current.get(carrierId);
    if (existing) return existing;
    if (seededCarrierIds.current.has(carrierId)) {
      return {
        noteContent: "",
        saveState: "saved",
        lastSavedTimestamp: null,
        hasContent: true,
      };
    }
    return createInitialState();
  }, []);

  const seedHasContent = useCallback(
    (carrierIds: string[]) => {
      let changed = false;
      for (const carrierId of carrierIds) {
        if (!seededCarrierIds.current.has(carrierId)) {
          seededCarrierIds.current.add(carrierId);
          changed = true;
        }
      }
      if (changed) tick();
    },
    [tick],
  );

  const setContent = useCallback(
    (carrierId: string, content: string) => {
      const prev = stateMap.current.get(carrierId) ?? createInitialState();
      stateMap.current.set(carrierId, {
        ...prev,
        noteContent: content,
        hasContent: content.trim().length > 0,
        saveState: "unsaved",
      });
      tick();
    },
    [tick],
  );

  const setSaveState = useCallback(
    (carrierId: string, state: SaveState) => {
      const prev = stateMap.current.get(carrierId) ?? createInitialState();
      stateMap.current.set(carrierId, { ...prev, saveState: state });
      tick();
    },
    [tick],
  );

  const markSaved = useCallback(
    (carrierId: string) => {
      const prev = stateMap.current.get(carrierId) ?? createInitialState();
      stateMap.current.set(carrierId, {
        ...prev,
        saveState: "saved",
        lastSavedTimestamp: Date.now(),
      });
      tick();
    },
    [tick],
  );

  const markError = useCallback(
    (carrierId: string) => {
      const prev = stateMap.current.get(carrierId) ?? createInitialState();
      stateMap.current.set(carrierId, { ...prev, saveState: "error" });
      tick();
    },
    [tick],
  );

  const initializeContent = useCallback(
    (carrierId: string, content: string) => {
      const existing = stateMap.current.get(carrierId);
      // Don't overwrite local edits — only initialize if no state exists yet
      if (existing) return;
      stateMap.current.set(carrierId, {
        noteContent: content,
        saveState: "saved",
        lastSavedTimestamp: null,
        hasContent: content.trim().length > 0,
      });
      tick();
    },
    [tick],
  );

  const hasUnsavedChanges = useCallback(
    (): boolean =>
      ![...stateMap.current.values()].every(
        (state) =>
          state.saveState !== "unsaved" && state.saveState !== "saving",
      ),
    [],
  );

  const getUnsavedCarrierIds = useCallback(
    (): string[] =>
      [...stateMap.current.entries()]
        .filter(([, state]) => state.saveState === "unsaved")
        .map(([id]) => id),
    [],
  );

  return {
    getState,
    setContent,
    setSaveState,
    markSaved,
    markError,
    initializeContent,
    seedHasContent,
    hasUnsavedChanges,
    getUnsavedCarrierIds,
    tick,
  };
};
